import {
  useEffect,
  useState,
} from 'react';
import {
  Auth0Client,
} from '@auth0/auth0-spa-js';
import {
  auth0Domain,
  auth0ClientId,
  auth0Audience,
} from '@/env';

let singletonClient: Auth0Client | null = null;

export default function useAuth0Client() {
  const [client, setClient] = useState<Auth0Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function initializeClient(mounted: boolean) {
    try {
      if (!singletonClient && typeof window !== 'undefined') {
        singletonClient = new Auth0Client({
          domain: auth0Domain,
          clientId: auth0ClientId,
          authorizationParams: {
            audience: auth0Audience,
            scope: 'openid profile email offline_access',
            redirect_uri: `${window.location.origin}/authorize`,
          },
          cacheLocation: 'localstorage',
          useRefreshTokens: true,
          useRefreshTokensFallback: true,
        });
      }

      try {
        await singletonClient?.getTokenSilently({
          authorizationParams: {
            audience: auth0Audience,
            scope: 'openid profile email offline_access',
          },
        });
      } catch (e: unknown) {
        const err = e as { error?: string; code?: string; message?: string };
        const code = err?.error ?? err?.code ?? '';
        const message = err?.message ?? '';
        const isExpected = (
          code === 'login_required' ||
          code === 'consent_required' ||
          code === 'interaction_required' ||
          code === 'missing_refresh_token' ||
          message.includes('login_required') ||
          message.includes('consent_required') ||
          message.includes('interaction_required') ||
          message.includes('missing_refresh_token')
        );
        if (!isExpected) {
          throw e;
        }
      }

      if (mounted && singletonClient) {
        setClient(singletonClient);
        setIsLoading(false);
      }
    } catch (e: unknown) {
      if (mounted) {
        setError(e instanceof Error ? e.message : 'Failed to initialize Auth0 client');
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
    let mounted = true;
    initializeClient(mounted);
    return () => {
      mounted = false;
    };
  }, []);

  return { client, isLoading, error };
}
